import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';

import { useLiveWire } from '../wire';
import { parseMarkdown, type MarkdownSpan } from './markdown';
import { useChat, type ChatMessage, type ChatTab } from './store';
import styles from './chat.module.css';

const ZONE_CHAT_LIMIT = 500;
const WORLD_BROADCAST_LIMIT = 500;
const WORLD_PREFIX = '/world ';

/**
 * World chat pane — bridge-channel view (CLIENT_DESIGN §9).
 *
 * Two tabs:
 *   - Zone — zone chat (WorldHost plane). Shows `Spoke` utterances + zone
 *     system messages.
 *   - `/world` — world-broadcast channel (Gateway plane). Yellow text,
 *     global 200-message buffer per CLIENT_DESIGN §12.1.
 *
 * Compose routes per active tab:
 *   Zone    → `WorldIntent::ZoneChat`        (via live-wire store)
 *   /world  → `GatewayIntent::WorldBroadcast`
 *   Zone tab + "/world " prefix → override to WorldBroadcast with the
 *   rest of the string stripped.
 */
export function WorldChatPane(): JSX.Element {
  const activeTab = useChat((s) => s.activeTab);
  const zone = useChat((s) => s.zone);
  const world = useChat((s) => s.world);
  const composeText = useChat((s) => s.composeText);
  const setActiveTab = useChat((s) => s.setActiveTab);
  const setComposeText = useChat((s) => s.setComposeText);
  const appendZoneMessage = useChat((s) => s.appendZoneMessage);
  const appendWorldBroadcast = useChat((s) => s.appendWorldBroadcast);

  const send = useLiveWire((s) => s.send);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const messages = activeTab === 'zone' ? zone : world;

  useEffect(() => {
    // Auto-scroll to newest on append. A later refinement pins the
    // scroll position when the user has scrolled up (Discord-style).
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleSubmit = (): void => {
    const raw = composeText.trim();
    if (!raw) return;

    const isBroadcastOverride =
      activeTab === 'zone' && raw.startsWith(WORLD_PREFIX);
    const text = isBroadcastOverride ? raw.slice(WORLD_PREFIX.length) : raw;
    if (!text) return;

    if (activeTab === 'world' || isBroadcastOverride) {
      const truncated = text.slice(0, WORLD_BROADCAST_LIMIT);
      const sent = send({
        plane: 'Gateway',
        intent: {
          kind: 'WorldBroadcast',
          data: { text: truncated },
        },
      });
      if (!sent) return; // rate-limited; throttle already pushed a toast
      appendWorldBroadcast({
        kind: 'broadcast',
        speaker: null,
        text: truncated,
        tick: 0n,
      });
    } else {
      const truncated = text.slice(0, ZONE_CHAT_LIMIT);
      // ZoneChat is a WorldIntent variant per P1.8 C5 alignment — the
      // pre-P1.4 stub that placed it on the Gateway plane is retired.
      const sent = send({
        plane: 'World',
        intent: { ZoneChat: { text: truncated } },
      });
      if (!sent) return;
      appendZoneMessage({
        kind: 'utterance',
        speaker: null,
        text: truncated,
        tick: 0n,
      });
    }

    setComposeText('');
  };

  return (
    <div className={styles.pane}>
      <div className={styles.tabs} role="tablist">
        <TabButton
          label="Zone"
          active={activeTab === 'zone'}
          onClick={() => setActiveTab('zone')}
        />
        <TabButton
          label="/world"
          active={activeTab === 'world'}
          onClick={() => setActiveTab('world')}
        />
      </div>
      <div
        ref={scrollRef}
        className={styles.scroll}
        role="log"
        aria-live="polite"
      >
        {messages.length === 0 ? (
          <div className={styles.empty}>
            {activeTab === 'zone'
              ? 'No zone messages yet. Say something in chat and your avatar will speak it.'
              : 'No /world broadcasts yet. Type here to reach everyone in the world.'}
          </div>
        ) : (
          messages.map((msg) => <MessageRow key={msg.id} msg={msg} />)
        )}
      </div>
      <form
        className={styles.composeRow}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <input
          type="text"
          value={composeText}
          onChange={(e) => setComposeText(e.target.value)}
          placeholder={placeholderFor(activeTab)}
          maxLength={
            activeTab === 'world' ? WORLD_BROADCAST_LIMIT : ZONE_CHAT_LIMIT
          }
          className={styles.composeInput}
          aria-label={`${activeTab} compose`}
        />
        <button type="submit" className={styles.composeButton}>
          Send
        </button>
      </form>
    </div>
  );
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={clsx(styles.tab, active && styles.tabActive)}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function MessageRow({ msg }: { msg: ChatMessage }): JSX.Element {
  if (msg.kind === 'system') {
    return <div className={styles.systemRow}>{msg.text}</div>;
  }
  return (
    <div className={clsx(styles.row, msg.kind === 'broadcast' && styles.rowBroadcast)}>
      <span className={styles.author}>{speakerLabel(msg)}</span>
      <span className={styles.body}>
        <InlineMarkdown text={msg.text} />
      </span>
    </div>
  );
}

function InlineMarkdown({ text }: { text: string }): JSX.Element {
  const spans = useMemo<MarkdownSpan[]>(() => parseMarkdown(text), [text]);
  return (
    <>
      {spans.map((span, idx) => {
        switch (span.kind) {
          case 'bold':
            return <strong key={idx}>{span.text}</strong>;
          case 'italic':
            return <em key={idx}>{span.text}</em>;
          case 'code':
            return (
              <code key={idx} className={styles.inlineCode}>
                {span.text}
              </code>
            );
          default:
            return <span key={idx}>{span.text}</span>;
        }
      })}
    </>
  );
}

function speakerLabel(msg: ChatMessage): string {
  if (!msg.speaker) return msg.kind === 'broadcast' ? '/world' : 'You';
  if ('Player' in msg.speaker) return `player-${msg.speaker.Player}`;
  if ('Soma' in msg.speaker) return `soma-${msg.speaker.Soma}`;
  return '?';
}

function placeholderFor(tab: ChatTab): string {
  return tab === 'zone'
    ? "Say something (or start with '/world' to broadcast)"
    : 'Broadcast to the world…';
}

// TabButton + MessageRow re-export slot (for future testing). Used by the
// Shell component tests to verify the chat pane renders on the bridge
// channel.
export { TabButton, MessageRow };
